import { useEffect, useRef } from 'react'
import { CalendarCheck, CalendarX, X } from 'lucide-react'
import { OutcomeRow } from '../../scoring/index.js'
import { Button } from '../../core/index.js'
import { pluralPoints } from './bridgeText.js'
import type { ScheduleDay } from './scheduleCalendarData.js'
import { ORIGIN_LABEL, REMOVED_LABEL } from './scheduleDayMarks.js'

/** What the calendar lets the worker do about a Clinical Bridge day; absent when they cannot enroll at all. */
export interface DayEnrollment {
  /** Whether this date may be enrolled for: today or later, inside this period or the next. */
  enrollable: (date: string) => boolean
  onEnroll: (date: string) => void
  onWithdraw: (date: string) => void
  /** True while an enroll or withdraw is in flight. */
  pending: boolean
  /** The API's refusal, in its own words, for the date last acted on. */
  error: string | null
  /** The facility's boost per Bridge shift worked, for the enroll button's hint. */
  scoreBoostPerShift: number
}

export function formatDay(date: string): string {
  return new Date(`${date}T00:00:00`).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })
}

function formatShortDate(iso: string): string {
  return new Date(iso.length === 10 ? `${iso}T00:00:00` : iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function periodLabel(period: string): string {
  return period === 'day' ? 'Day' : period === 'evening' ? 'Evening' : period === 'night' ? 'Night' : period
}

/** A unit as the facility names it; one it does not list, `unit-a` as "Unit A" and any other id as it is. */
function unitLabel(unitId: string, names: ReadonlyMap<string, string>): string {
  return names.get(unitId) ?? (unitId.startsWith('unit-') ? `Unit ${unitId.slice('unit-'.length).toUpperCase()}` : unitId)
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

/**
 * What happened to this day since the release (worker calendar design, 26 October 2026): how it came to the worker,
 * what was taken off, what is offered or was lost, and the PTO standing.
 */
function ChangeSection({ day, unitNames }: { day: ScheduleDay; unitNames: ReadonlyMap<string, string> }) {
  const mark = day.mark
  if (!mark) {
    return null
  }
  const lines: { text: string; coral: boolean }[] = [
    ...(mark.origin ? [{ text: `${ORIGIN_LABEL[mark.origin.kind]}${mark.origin.at ? ` · ${formatTime(mark.origin.at)}` : ''}.${mark.origin.note ? ` ${mark.origin.note}` : ''}`, coral: mark.origin.kind === 'against' }] : []),
    ...(mark.awayUnitId ? [{ text: `${unitLabel(mark.awayUnitId, unitNames)} is not your home unit.`, coral: false }] : []),
    ...(mark.removed
      ? [
          {
            text: `${REMOVED_LABEL[mark.removed.kind]}: ${periodLabel(mark.removed.shiftType)} ${mark.removed.startTime}–${mark.removed.endTime}, ${unitLabel(mark.removed.unitId, unitNames)} · ${formatTime(mark.removed.at)}.${mark.removed.reason ? ` ${mark.removed.reason}.` : ''}`,
            coral: true,
          },
        ]
      : []),
    ...(mark.removed?.offeredBack ? [{ text: `It is offered back to you${mark.removed.recallUntil ? ` until ${formatTime(mark.removed.recallUntil)}` : ''}: take it back or let it go below the calendar.`, coral: true }] : []),
    ...(mark.offered ? [{ text: 'A shift is offered to you this day. Answer it under Waiting for your answer, below the calendar.', coral: true }] : []),
    ...(mark.lost ? [{ text: 'You asked for this day; it went to someone ranked ahead. Your reliability score is your rank.', coral: false }] : []),
    ...(mark.pto === 'approved' ? [{ text: 'PTO approved.', coral: false }] : mark.pto === 'requested' ? [{ text: 'PTO requested; your facility decides.', coral: false }] : []),
  ]
  if (lines.length === 0) {
    return null
  }
  return (
    <section className="border-b border-marine/10 bg-marine-lt/50 px-4 py-3">
      <div className="mb-1 text-[9px] font-bold uppercase tracking-wide text-ink-mid">Since the release</div>
      <ul className="flex flex-col gap-1">
        {lines.map((line) => (
          <li key={line.text} className={`text-[11px] leading-snug ${line.coral ? 'text-coral' : 'text-ink'}`}>
            {line.text}
          </li>
        ))}
      </ul>
    </section>
  )
}

/** The Clinical Bridge part of a day: enrolled or not, and the one button that changes it where the worker may. */
function BridgeSection({ day, enrollment }: { day: ScheduleDay; enrollment: DayEnrollment | null }) {
  const canAct = enrollment !== null && enrollment.enrollable(day.date)
  if (!day.bridge && !canAct) {
    return null
  }
  return (
    <section className="border-b border-marine/10 px-4 py-3">
      <div className="mb-1 text-[9px] font-bold uppercase tracking-wide text-coral">Clinical Bridge</div>
      {day.bridge ? (
        <div className="text-[11px] text-ink">
          You are enrolled to cover a gap this day.
          {day.bridge.extraCredit > 0 && <> Working it adds {pluralPoints(day.bridge.extraCredit)} to your score.</>}
          {day.bridge.enrolledBy === 'import' && <> Your facility put this day on your list.</>}
          {day.bridge.enrolledBy === 'partner' && <> Your scheduler put this day on your list from your bid.</>}
        </div>
      ) : (
        <div className="text-[11px] text-ink">
          Enroll to be told if a shift opens this day. There is no penalty for declining one.
          {enrollment !== null && enrollment.scoreBoostPerShift > 0 && <> Working it adds {pluralPoints(enrollment.scoreBoostPerShift)} to your score.</>}
        </div>
      )}
      {canAct && enrollment && (
        <div className="mt-2.5 flex flex-col gap-1.5">
          {day.bridge ? (
            <Button icon={CalendarX} variant="secondary" size="sm" disabled={enrollment.pending} onClick={() => enrollment.onWithdraw(day.date)}>
              {enrollment.pending ? 'Withdrawing…' : 'Withdraw from this day'}
            </Button>
          ) : (
            <Button icon={CalendarCheck} size="sm" disabled={enrollment.pending} onClick={() => enrollment.onEnroll(day.date)}>
              {enrollment.pending ? 'Enrolling…' : 'Enroll for this day'}
            </Button>
          )}
          {enrollment.error && (
            <div role="alert" className="text-[11px] text-coral">
              {enrollment.error}
            </div>
          )}
        </div>
      )}
      {day.bridge && !canAct && <div className="mt-1 text-[10px] text-ink-mid">A day that has passed stays on your record.</div>}
    </section>
  )
}

interface ScheduleDayDetailsProps {
  day: ScheduleDay
  id: string
  enrollment: DayEnrollment | null
  /** The facility's units by id, named as it calls them. */
  unitNames?: ReadonlyMap<string, string>
  onClose: () => void
}

/**
 * One date in full, over the page: the worker's own shifts that day, the
 * open shifts, their Clinical Bridge enrollment (with the enroll or
 * withdraw button where they may) and a holiday event. Closed by its
 * button, Escape or a tap outside.
 */
export function ScheduleDayDetails({ day, id, enrollment, unitNames = new Map(), onClose }: ScheduleDayDetailsProps) {
  const closeRef = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    closeRef.current?.focus()
    function onKey(event: KeyboardEvent): void {
      if (event.key === 'Escape') {
        onClose()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-ink/40 p-4" onClick={onClose}>
      <div id={id} role="dialog" aria-modal="true" aria-label={formatDay(day.date)} onClick={(event) => event.stopPropagation()} className="max-h-[80vh] w-full max-w-sm overflow-y-auto border border-marine/30 bg-white shadow-xl">
        <div className="flex items-center gap-2 border-b border-marine/10 px-4 py-3">
          <div className="text-sm font-extrabold text-ink">{formatDay(day.date)}</div>
          <button ref={closeRef} type="button" onClick={onClose} aria-label="Close" className="ml-auto text-ink-mid hover:text-ink">
            <X aria-hidden="true" className="h-4 w-4" />
          </button>
        </div>

        <ChangeSection day={day} unitNames={unitNames} />

        {day.scheduled && day.outcomes.length === 0 && (
          <section className="border-b border-marine/10 px-4 py-3">
            <div className="mb-1.5 text-[9px] font-bold uppercase tracking-wide text-ink-mid">On your schedule</div>
            <div className="flex items-baseline justify-between gap-3 border border-marine bg-marine/5 px-2.5 py-2 text-[11px]">
              <span className="font-bold text-ink">{periodLabel(day.scheduled.shiftType)}</span>
              <span className="text-ink-mid">
                {day.scheduled.startTime}–{day.scheduled.endTime} · {unitLabel(day.scheduled.unitId, unitNames)}
              </span>
            </div>
          </section>
        )}

        {day.drafted && !day.scheduled && day.outcomes.length === 0 && (
          <section className="border-b border-marine/10 px-4 py-3">
            <div className="mb-1.5 text-[9px] font-bold uppercase tracking-wide text-ink-mid">On your draft</div>
            <div className="flex items-baseline justify-between gap-3 border border-dashed border-marine bg-marine/5 px-2.5 py-2 text-[11px]">
              <span className="font-bold text-ink">{periodLabel(day.drafted.shiftType)}</span>
              <span className="text-ink-mid">
                {day.drafted.startTime}–{day.drafted.endTime} · {unitLabel(day.drafted.unitId, unitNames)}
              </span>
            </div>
            <div className="mt-1 text-[10px] text-ink-mid">What you asked for. It is yours once your facility releases the month.</div>
          </section>
        )}

        {day.outcomes.length > 0 && (
          <section className="border-b border-marine/10">
            <div className="px-4 pt-3 text-[9px] font-bold uppercase tracking-wide text-ink-mid">Your shift</div>
            <ul className="divide-y divide-marine/10">
              {day.outcomes.map((outcome) => (
                <OutcomeRow key={outcome.eventId} outcome={outcome} />
              ))}
            </ul>
          </section>
        )}

        {day.available.length > 0 && (
          <section className="border-b border-marine/10 px-4 py-3">
            <div className="mb-1.5 text-[9px] font-bold uppercase tracking-wide text-ink-mid">Open shifts</div>
            <ul className="flex flex-col gap-1.5">
              {day.available.map((shift) => (
                <li key={shift.id} className="flex items-baseline justify-between gap-3 border border-marine/20 px-2.5 py-2 text-[11px]">
                  <span className="font-bold text-ink">{periodLabel(shift.period)}</span>
                  <span className="text-ink-mid">
                    {shift.startTime}–{shift.endTime} · {unitLabel(shift.unitId, unitNames)}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}

        <BridgeSection day={day} enrollment={enrollment} />

        {day.holiday && (
          <section className="bg-gold/10 px-4 py-3">
            <div className="mb-1 text-[9px] font-bold uppercase tracking-wide text-[#8B6F20]">Holiday bid event · {day.holiday.name}</div>
            <div className="text-[11px] text-ink">
              Complete {day.holiday.shiftsRequiredForBonus} shifts between {formatShortDate(day.holiday.periodStart)} and {formatShortDate(day.holiday.periodEnd)} to earn a ${day.holiday.bonusAmount} bonus.
            </div>
          </section>
        )}
      </div>
    </div>
  )
}
